import { type ReactNode, useEffect, useState } from "react";
import { z } from "zod";
import { ErrorButton } from "../../../../../../../components/Button/ErrorButton.tsx";
import { ErrorMessage } from "../../../../../../../components/Error/ErrorMessage.tsx";
import { Form } from "../../../../../../../components/Form/Form.tsx";
import { useForm } from "../../../../../../../components/Form/FormContext.ts";
import { Icon } from "../../../../../../../components/Icon/Icon.tsx";
import { Toggle } from "../../../../../../../components/Toggle/Toggle.tsx";
import { useDebugMode } from "../../../../../../components/DebugMode/hooks/useDebugMode.ts";
import {
  CHARACTER_MODEL_TYPE,
  OBJECT_MODEL_TYPE,
} from "../../../../../../constants.ts";
import { PanelLayout } from "../../../../../components/Layout/PanelLayout.tsx";
import { PanelSection } from "../../../../../components/Panel/PanelSection.tsx";
import { ActionsBar } from "../../../../../components/Panel/Settings/ActionsBar.tsx";
import { ModelSelectionSection } from "../../../../../components/Panel/Settings/ModelSelectionSection.tsx";
import { PromptSection } from "../../../../../components/Panel/Settings/PromptSection.tsx";
import { SliderSubsection } from "../../../../../components/Panel/SliderSubsection.tsx";
import { WrappedPanelSection } from "../../../../../components/Panel/WrappedPanelSection.tsx";
import { useBoardUuid } from "../../../../../hooks/useBoardUuid.ts";
import { useSelectedAsset } from "../../../../../hooks/useSelectedAsset.ts";
import { scrollToEditionHistoryStore } from "../../../../../stores/scrollToEditionHistoryStore.ts";
import { getErrorsFiltered } from "../../../../../utils/getErrorsFiltered.ts";
import { usePromptWarnings } from "../../../../hooks/usePromptWarnings.ts";
import { useCanvasEventsListener } from "../../../Designer/hooks/useCanvasEventsListener.ts";
import {
  DEFAULT_GENERATIVE_FILL_MODEL,
  DIFFERENTIAL_DIFFUSION_GENERATIVE_FILL_CREATIVITY_STRENGTH_PRESETS,
  DIFFERENTIAL_DIFFUSION_GENERATIVE_FILL_MODEL,
  GENERATIVE_FILL_MODEL_TO_LABELS,
  GENERATIVE_FILL_MODELS,
} from "./constants.ts";
import { useGenerativeFillSettings } from "./hooks/useGenerativeFillSettings.ts";
import { useUploadAndGenerativeFillMutation } from "./hooks/useUploadAndGenerativeFillMutation.ts";
import {
  type FillMode,
  generativeFillCanvasStore,
} from "./stores/generativeFillCanvasStore.ts";
import {
  generateBrushMask,
  setBrushFreeDrawingBrush,
  updateExistingBrushColors,
} from "./utils/brush.ts";
import {
  generateLassosMask,
  setLassoFreeDrawingBrush,
  setOnCreateLasso,
  updateExistingLassosColors,
} from "./utils/lasso.ts";

export const GenerativeFillSettings = () => {
  const { mutate: fillMutate, isLoading: fillIsLoading } =
    useUploadAndGenerativeFillMutation({
      onSuccess: () => {
        scrollToEditionHistoryStore.triggerScrollToEditionHistory();
      },
    });

  const { fabricCanvas, fillMode } = generativeFillCanvasStore.useState();

  return (
    <Form
      schema={zGenerativeFillSettings}
      initialValues={{
        selectedAssetUuid: "",
        prompt: "",
        isCanvasEmpty: true,
        isSelectedImageSucceeded: false,
        areSelectedModelsValid: false,
      }}
      onSubmit={async (values) => {
        if (fabricCanvas) {
          const maskBlob = await (fillMode === "lasso"
            ? generateLassosMask({ fabricCanvas })
            : generateBrushMask({ fabricCanvas }));

          fillMutate({
            mask: maskBlob,
            assetUuid: values.selectedAssetUuid,
          });
        }
      }}
    >
      <GenerativeFillSettingsFormContent isGenerationLoading={fillIsLoading} />
    </Form>
  );
};

const zGenerativeFillSettings = z.object({
  selectedAssetUuid: z
    .string({ required_error: "Please upload an image in the canvas" })
    .uuid(),
  prompt: z.string().min(1, { message: "Please enter a prompt" }),
  isCanvasEmpty: z.boolean().refine((val) => !val, {
    message: "Please trace a zone on the image",
  }),
  areSelectedModelsValid: z.boolean().refine((val) => val, {
    message:
      "The selected models are not valid, please try again by reseting the selection.",
  }),
  isSelectedImageSucceeded: z.boolean().refine((val) => val, {
    message: "Please wait for your image to be generated",
  }),
});
type GenerativeFillSettingsValues = z.input<typeof zGenerativeFillSettings>;

const SelectionType: {
  key: FillMode;
  element: ReactNode;
}[] = [
  {
    key: "brush",
    element: <Icon name="Paintbrush" />,
  },
  {
    key: "lasso",
    element: <Icon name="LassoSelect" />,
  },
];

const GenerativeFillSettingsFormContent = ({
  isGenerationLoading,
}: {
  isGenerationLoading: boolean;
}) => {
  const { fabricCanvas, fillMode, brushSize } =
    generativeFillCanvasStore.useState();
  const [isCanvasEmpty, setIsCanvasEmpty] = useState<boolean>(false);

  const boardUuid = useBoardUuid();

  const { tool, settings, setSettings, areSelectedModelsValid, modelActions } =
    useGenerativeFillSettings(boardUuid);

  const { selectedAsset } = useSelectedAsset();

  const { setValues, submit, useError } =
    useForm<GenerativeFillSettingsValues>();
  const missingAssetError = useError((v) => v.selectedAssetUuid);
  const modelsNotValidError = useError((v) => v.areSelectedModelsValid);
  let imageNotSucceededError = useError((v) => v.isSelectedImageSucceeded);
  let isCanvasEmptyError = useError((v) => v.isCanvasEmpty);
  if (imageNotSucceededError) isCanvasEmptyError = undefined;
  if (missingAssetError) imageNotSucceededError = undefined;

  const promptEmptyError = useError((v) => v.prompt);

  // XXX: As fabric canvas does not force rerender when an object is added or removed,
  // we need to listen to the events to update the isCanvasEmpty state.
  useCanvasEventsListener({
    canvas: fabricCanvas,
    eventTypes: ["object:added", "object:removed"],
    onEvent: () => {
      setIsCanvasEmpty(fabricCanvas?.isEmpty() ?? false);
    },
  });

  useEffect(() => {
    setValues({
      areSelectedModelsValid,
    });
  }, [setValues, areSelectedModelsValid]);

  useEffect(() => {
    setValues({ selectedAssetUuid: selectedAsset?.uuid });
  }, [setValues, selectedAsset?.uuid]);

  useEffect(() => {
    setValues({
      isSelectedImageSucceeded: selectedAsset?.content.status === "SUCCEEDED",
    });
  }, [setValues, selectedAsset?.content.status]);

  useEffect(() => {
    setValues({ prompt: settings.prompt });
  }, [setValues, settings.prompt]);

  useEffect(() => {
    if (fabricCanvas) {
      setValues({ isCanvasEmpty: true });
      const onAdd = () => setValues({ isCanvasEmpty: false });
      const onRemove = () =>
        setValues({ isCanvasEmpty: fabricCanvas.getObjects().length === 0 });

      fabricCanvas.on("object:added", onAdd);
      fabricCanvas.on("object:removed", onRemove);
      return () => {
        fabricCanvas.off("object:added", onAdd);
        fabricCanvas.off("object:removed", onRemove);
      };
    }
  }, [fabricCanvas, setValues]);

  useEffect(() => {
    if (fabricCanvas) generativeFillCanvasStore.clearObjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAsset?.uuid]);

  // XXX: to change the selection opacity in fabric canvas
  useEffect(() => {
    if (fabricCanvas) {
      const fillColor = "rgba(2, 159, 245, 0.75)";
      if (fillMode === "lasso") {
        setLassoFreeDrawingBrush({ fabricCanvas });
        const onCreateLassoCleanUp = setOnCreateLasso({
          fabricCanvas,
          color: fillColor,
        });
        updateExistingLassosColors({
          fabricCanvas,
          color: fillColor,
        });
        return onCreateLassoCleanUp;
      } else {
        setBrushFreeDrawingBrush({
          fabricCanvas,
          color: fillColor,
          size: brushSize,
        });
        updateExistingBrushColors({ fabricCanvas, color: fillColor });
      }
    }
  }, [brushSize, fabricCanvas, fillMode]);

  const warnings = usePromptWarnings({
    prompt: settings.prompt,
    selectedModels: settings.selectedModels.models,
  });

  const isDebugModeOn = useDebugMode();

  return (
    <PanelLayout
      bottomChildren={
        <ActionsBar
          isLoading={isGenerationLoading}
          onPrimaryActionClick={submit}
          primaryActionButtonContent="Fill"
          intercomArticle="change_part_of_your_image"
          errors={getErrorsFiltered([
            imageNotSucceededError,
            modelsNotValidError,
          ])}
          errorsChildren={
            modelsNotValidError && (
              <ErrorButton
                onClick={() => {
                  modelActions.reset();
                }}
              >
                Reset model selection
              </ErrorButton>
            )
          }
          onSecondaryActionClick={() => {
            generativeFillCanvasStore.clearObjects();
          }}
          isSecondaryActionDisabled={isCanvasEmpty}
          secondaryActionButtonContent="Clear selection"
        />
      }
    >
      <PanelSection
        title="Selection"
        rightItem={
          <Toggle
            items={SelectionType.map((item) => (
              <div key={item.key} className="flex-row-center gap-100">
                {item.element}
                <div className="label-md-default capitalize">{item.key}</div>
              </div>
            ))}
            index={SelectionType.map((item) => item.key).indexOf(fillMode)}
            onIndexChange={(index) =>
              generativeFillCanvasStore.setFillMode(SelectionType[index].key)
            }
          />
        }
      >
        {fillMode === "brush" && fabricCanvas && (
          <SliderSubsection
            sliderName="Brush size"
            min={24}
            max={124}
            displayValue={false}
            value={brushSize}
            onChange={(value) => {
              if (fabricCanvas.freeDrawingBrush) {
                fabricCanvas.freeDrawingBrush.width = value;
              }
              generativeFillCanvasStore.setBrushSize(value);
            }}
          />
        )}
        {isCanvasEmptyError && <ErrorMessage text={isCanvasEmptyError} />}
      </PanelSection>
      <PromptSection
        sectionTitle="Fill with"
        prompt={settings.prompt}
        setPrompt={(newPrompt) => {
          setSettings({
            prompt: newPrompt,
          });
        }}
        warnings={warnings}
        placeholder="Describe what you want to generate in your selection"
        onEnterKeyDown={submit}
        errors={getErrorsFiltered([promptEmptyError])}
      />
      <ModelSelectionSection
        tool={tool}
        modelTypes={[CHARACTER_MODEL_TYPE, OBJECT_MODEL_TYPE]}
        extraModelSpecificComponents={{
          [CHARACTER_MODEL_TYPE]: settings.selectedModels
            .hasExactlyOneCharacterModelSelected && (
            <DifferentialDiffusionModelSpecificComponents />
          ),
          [OBJECT_MODEL_TYPE]: settings.selectedModels
            .hasExactlyOneObjectModelSelected && (
            <DifferentialDiffusionModelSpecificComponents />
          ),
        }}
      />
      {isDebugModeOn && (
        <WrappedPanelSection title="Debug parameters">
          <div className="flex-col gap-150">
            <Toggle
              items={GENERATIVE_FILL_MODELS.map((model) => (
                <div key={model} className="capitalize">
                  {GENERATIVE_FILL_MODEL_TO_LABELS[model]}
                </div>
              ))}
              index={GENERATIVE_FILL_MODELS.indexOf(settings.model_used)}
              onIndexChange={(index) => {
                setSettings({
                  model_used: GENERATIVE_FILL_MODELS[index],
                });
              }}
            />
            <SliderSubsection
              value={Math.round(settings.guidance_scale * 100)}
              min={0}
              max={100}
              onChange={(value) =>
                setSettings({
                  guidance_scale: value / 100,
                })
              }
              sliderName="Guidance Scale"
              sliderInformationSection={
                <div className="flex-col text-primary body-md-default gap-200">
                  <div>
                    Following range will be applied to the guidance scale:
                  </div>
                  <div>
                    - <span className="body-md-semibold">Flux (Photo)</span>: 0%
                    : 2.5, 100% : 4.5
                  </div>
                  <div>
                    -{" "}
                    <span className="body-md-semibold">
                      Flux (Illustration)
                    </span>
                    : 0% : 4, 100% : 6
                  </div>
                </div>
              }
            />
          </div>
        </WrappedPanelSection>
      )}
    </PanelLayout>
  );
};

const DifferentialDiffusionModelSpecificComponents = () => {
  const boardUuid = useBoardUuid();

  const { settings, setSettings } = useGenerativeFillSettings(boardUuid);

  return (
    (settings.model_used === DIFFERENTIAL_DIFFUSION_GENERATIVE_FILL_MODEL ||
      (settings.model_used === DEFAULT_GENERATIVE_FILL_MODEL &&
        (settings.selectedModels.hasExactlyOneCharacterModelSelected ||
          settings.selectedModels.hasExactlyOneObjectModelSelected))) && (
      <PanelSection
        title="Strength"
        infoContent={
          <div className="flex-col body-md-default text-primary gap-200">
            {settings.selectedModels.hasExactlyOneCharacterModelSelected
              ? "Strength controls how much the AI changes the original image to match your character. Higher values mean more changes."
              : settings.selectedModels.hasExactlyOneObjectModelSelected
              ? "Strength controls how much the AI changes the original image to match your object. Higher values mean more changes."
              : "Strength controls how much the AI changes the original image to match your prompt and style. Higher values mean more changes."}
          </div>
        }
      >
        <div className="flex-col-center gap-200">
          <Toggle
            items={DIFFERENTIAL_DIFFUSION_GENERATIVE_FILL_CREATIVITY_STRENGTH_PRESETS.map(
              (preset) => (
                <div key={preset} className="capitalize">
                  {preset}
                </div>
              ),
            )}
            index={DIFFERENTIAL_DIFFUSION_GENERATIVE_FILL_CREATIVITY_STRENGTH_PRESETS.indexOf(
              settings.differential_diffusion_settings
                .creativity_strength_preset,
            )}
            onIndexChange={(index) =>
              setSettings({
                differential_diffusion_settings: {
                  creativity_strength_preset:
                    DIFFERENTIAL_DIFFUSION_GENERATIVE_FILL_CREATIVITY_STRENGTH_PRESETS[
                      index
                    ],
                  prompt_strength:
                    settings.differential_diffusion_settings.prompt_strength ??
                    0.5,
                },
              })
            }
            className="w-full justify-between"
          />
          {settings.differential_diffusion_settings
            .creativity_strength_preset === "CUSTOM" && (
            <div className="w-full flex-col gap-150">
              <SliderSubsection
                min={0}
                max={100}
                value={Math.round(
                  (settings.differential_diffusion_settings.prompt_strength ??
                    0.5) * 100,
                )}
                onChange={(promptStrength: number) =>
                  setSettings({
                    differential_diffusion_settings: {
                      creativity_strength_preset: "CUSTOM",
                      prompt_strength: promptStrength / 100,
                    },
                  })
                }
              />
            </div>
          )}
        </div>
      </PanelSection>
    )
  );
};
